<template>
  <v-container class="ForgotPassword-container mx-auto">
    <LoginTopbar />

    <v-row class="ForgotPassword-content">
      <v-col class="d-flex justify-center">
         <AppCard class="ForgotPassword-card">
           <div class="d-flex flex-column row-gap-6">
              <h2>{{ $t('login.views.ForgotPassword.title') }}</h2>

             <AppTextField v-model.trim="login.email"
                           :label="$t('login.views.ForgotPassword.email')"
                           type="email"
                           class="ForgotPassword-input"
                           :error-messages="emailErrors"
                           maxlength="255"
                           @blur="validateEmail"
                           @keypress.enter="prepareForgotPassword"
             />

             <app-button
               class="align-self-center"
               :loading="loading"
               @click="prepareForgotPassword"
             >
               {{$t('login.views.ForgotPassword.resetPassword')}}
             </app-button>

             <template v-if="success && !submitButtonDisabled">
               <v-divider />
               <div class="d-flex align-center text-text-secondary">
                 <app-material-icon
                  name="check_circle"
                  filled
                  color="icon-success"
                  class="mr-2"
                 />
                 <span>{{$t('login.views.ForgotPassword.successMsg')}}</span>
               </div>
             </template>
           </div>
        </AppCard>
      </v-col>
    </v-row>

    <div class="d-flex justify-center mt-6">
      <BackToClosd />
    </div>
  </v-container>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import { createNamespacedHelpers } from 'vuex'

import AppCard from '@/common/AppCard.vue'
import AppTextField from '@/common/AppTextField.vue'
import { formFieldErrors } from '@/common/utils/form'
import { toast } from '@/common/utils/toast'
import { Login } from '@/models/login.model'
import { FORGOT_PASSWORD } from '@/store/modules/login/action_types'
import { postmarkEmail } from '@/validators/postmarkEmail'

import BackToClosd from '../BackToClosd.vue'
import LoginTopbar from '../LoginTopbar.vue'

const { mapState, mapActions } = createNamespacedHelpers('login')

export default {
  name: 'ForgotPassword',
  components: { LoginTopbar, AppCard, AppTextField, BackToClosd },
  setup: () => ({ v$: useVuelidate() }),
  validations: {
    login: {
      email: { required, postmarkEmail },
    },
  },
  data () {
    return {
      login: new Login(),
      show: false,
      success: false,
    }
  },
  computed: {
    ...mapState(['loading']),
    submitButtonDisabled () {
      return !this.v$.login.email.$dirty || this.v$.login.email.$errors.length
    },
    emailErrors () {
      return formFieldErrors(
          this.v$.login.email,
          [
            ['required', this.$t('common.validations.fieldRequired', { fieldName: this.$t('login.views.ForgotPassword.email') })],
            ['postmarkEmail', this.$t('common.validations.email')],
          ],
      )
    },
  },
  methods: {
    ...mapActions([FORGOT_PASSWORD]),
    validateEmail () {
      this.v$.login.email.$touch()
    },
    prepareForgotPassword () {
      if (this.loading) {
        return
      }
      this.v$.login.email.$touch()
      if (!this.v$.login.email.$invalid) {
        this.FORGOT_PASSWORD(this.login)
          .then(() => { this.success = true })
          .catch(error => {
            toast.error(this.$t('login.views.ForgotPassword.submitError'))
            throw error
          })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  @import "vuetify/settings";

  .ForgotPassword-container {
    max-width: 1100px;
    max-height: 840px;
  }
  .ForgotPassword-content{
    margin-top: 10%;
  }
  .ForgotPassword-card {
    padding: 25px 35px;
    max-height: 280px;
  }
  :deep(.ForgotPassword-input .v-input__slot) {
    background-color: #fff;
  }
  :deep(.v-btn.v-btn--disabled.v-btn--has-bg) {
    background-color: #ffe3e1 !important;
  }

  @media #{map-get($display-breakpoints, 'sm-and-down')}{
    .ForgotPassword-content{
      margin-top: 0;
    }
  }
</style>
